import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  INumHash 
} from "@models";
import { 
  Character,
  CharacterSpell,
  CharacterAbility,
  CharacterAction,
  SpellsFromList,
  CharacterSheetInterface,
  CharacterItem,
  Attack,
  StoredSpell,
  SpellsFromListWithCosts,
} from "@dndModels";
import { isSpellLevel, SpellLevel, SpellLevelString } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
// import DataUtilities from "../../../utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import StringBox from "@components/input/strings/StringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

import CharacterManageSpellsButton from "@dndComponents/character/spells_and_abilities/manage/CharacterManageSpellsButton";
import CharacterActionInput from "@dndComponents/character/spells_and_abilities/CharacterActionInput";
import CharacterResourceBoxes from "@dndComponents/character/misc/CharacterResourceBoxes";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterSpellDetails from "@dndComponents/character/spells_and_abilities/CharacterSpellDetails";
import CharacterItemDetailsSidebar from "@dndComponents/character/inventory/item/CharacterItemDetailsSidebar";
import CharacterAttackSidebar from "@dndComponents/character/inventory/CharacterAttackSidebar";
import CharacterSpellStats from "@dndComponents/character/spells_and_abilities/CharacterSpellStats";

interface Props {
  char_int: CharacterSheetInterface;
  char: Character;
  // campaign?: Campaign;
  disabled?: boolean;
  shared: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
// const common_util = DataUtilities.getInstance();

export default function CharacterSpells({
  char_int,
  char,
  disabled = false,
  shared,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloadingResources, setReloadingResources] = useState(false);
  const [ping, setPing] = useState(false);
  const [search_string, setSearchString] = useState("");
  const [view, setView] = useState<"ALL" | SpellLevelString | " Concentration " | " Ritual ">("ALL");
  const [levels, setLevels] = useState<INumHash>({});
  const [concentration, setConcentration] = useState<boolean | null>(null);
  const [ritual, setRitual] = useState<boolean | null>(null);
  // const [show_extra_effects, setShowExtraEffects] = useState(true);
  const [upcasts, setUpcasts] = useState(false);
  const [drawer, setDrawer] = useState("");
  const [details_action, setDetailsAction] = useState<CharacterAction | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const resourcesChanged = () => {
    // console.log("Spells");
    setReloadingResources(true);
  }

  const recalculated = () => {
    setReloading(true);
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      char_int.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
      char_int.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      char_int.subscription_holder.subscribe("Spells", mounted, recalculated);
      resetSearch();
    }
  });
  
  useEffect(() => {
    if (reloadingResources) {
      setReloadingResources(false);
    }
  }, [reloadingResources, setReloadingResources]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
      resetSearch();
    }
    // eslint-disable-next-line
  }, [reloading, setReloading]);

  const resetSearch = () => {
    let search_filtered = [...char_int.mind.spells, ...char_int.mind.spell_as_abilities, ...char_int.mind.ritual_only];
    if (search_string.length > 0) {
      search_filtered = api.filter("", search_filtered, { search_string });
    }
    // .filter(o => o.name.toLowerCase().includes(search_string.toLowerCase()));
    const new_levels: INumHash = {};
    let new_concentration = false;
    let new_ritual = false;
    // const spell_levels = common_util.unique_array(search_filtered.map(o => o.level)) as number[];
    // for (const spell_level of spell_levels) {
    //   new_levels[spell_level] = 1;
    // }
    if (search_filtered.filter(o => o.level === 0).length > 0) {
      new_levels[0] = 1;
    }
    for (const slot of char_int.body.use_slots) {
      if (!new_levels[slot.level]) {
        new_levels[slot.level] = 1;
      }
    }
    for (const char_spell of search_filtered) {
      if (char_spell instanceof CharacterAbility) {
        new_levels[char_spell.level] = 1;
      } else if (char_spell.spell) {
        if (char_spell.spell.concentration) {
          new_concentration = true;
        }
        if (char_spell.spell.ritual) {
          new_ritual = true;
        }
        if ((char_spell.at_will || char_spell.ritual_only) && !new_levels[char_spell.level]) {
          new_levels[char_spell.level] = 1;
        }
      }
    }
    setLevels(new_levels);
    setConcentration(new_concentration);
    setRitual(new_ritual);
  }

  const renderSpellStats = () => {
    if (char_int instanceof Character) {
      const classes = char_int.spirit.classes.filter(o => o.spellcasting_ability !== "None");
      return (
        <div className="centered">
          <Grid container spacing={0} direction="row">
            <Grid size={3}>
              &nbsp;
            </Grid>
            <Grid className="small_label gray" size={3}>
              Modifier
            </Grid>
            <Grid className="small_label gray" size={3}>
              Sp. Atk
            </Grid>
            <Grid className="small_label gray" size={3}>
              DC
            </Grid>
            { classes.map(char_class => {
              return (
                <Grid key={char_class.true_id} size={12}>
                  <CharacterSpellStats char={char_int} char_class={char_class} />
                </Grid>
              );
            })}
          </Grid>
        </div>
      );
    }
    return null;
  }

  const getName = (action: CharacterAction) => {
    const spell = action.action;
    if (spell) {
      if (spell instanceof CharacterItem) { 
        return char_util.getUseName(spell);
      } else if (spell instanceof Attack) {
        return "Unarmed Strike";
      } else {
        let name = spell.true_name;
        if ((spell instanceof CharacterAbility || spell instanceof CharacterSpell) && 
          spell._name !== "") {
          name = spell._name;
        } else if (spell instanceof CharacterAbility && 
          // spell.the_ability instanceof SpellAsAbility && 
          spell.the_spell) {
          name = spell.the_spell.name;
        }
        return name;
      }
    }
    return "";
  }

  const renderSpells = () => {
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "left"
          }}>
          <ToggleButtonBox 
            name="Upcasts"
            value={upcasts}
            onToggle={() => {
              setUpcasts(!upcasts);
            }}
          />
        </div>
        <div className="centered">
          { renderSpellFilters() }
        </div>
        { !reloading && renderSpellGroups() }
      </>
    );
  }

  const renderSpellFilters = () => {
    const options: string[] = [
      "ALL",
      ...Object.keys(levels).map(level => { return level === "0" ? "Cantrip" : `${level}`; })
    ];
    if (concentration) {
      options.push(" Concentration ");
    }
    if (ritual) {
      options.push(" Ritual ");  
    }
    return (
      <CenteredMenu
        options={options}
        selected={view}
        onSelect={(changed: string) => {
          setView(changed as "ALL" | SpellLevelString | " Concentration " | " Ritual ");
        }}
      />
    );
  }

  const renderSpellGroups = () => {
    let filtered = char_int.mind.actions.filter(o => ["Spell","Spell As Ability","Spell as Ability"].includes(o.type));
    if (search_string.length > 0) {
      filtered = filtered.filter(o => 
        o.name.toLowerCase().includes(search_string.toLowerCase()));
    }
    // console.log(filtered.filter(o => o.name === "Mental Prison"));
    if (view !== "ALL") {
      if (view === " Concentration ") {
        filtered = filtered.filter(o => o.action && (o.action instanceof CharacterSpell || o.action instanceof CharacterAbility || o.action instanceof StoredSpell) && o.action.the_spell && o.action.the_spell.concentration);
      } else if (view === " Ritual ") {
        filtered = filtered.filter(o => o.action && (o.action instanceof CharacterSpell || o.action instanceof CharacterAbility || o.action instanceof StoredSpell) && o.action.the_spell && o.action.the_spell.ritual);
      } else if (view === "Cantrip") {
        filtered = filtered.filter(o => o.action && (o.action instanceof CharacterSpell || o.action instanceof CharacterAbility || o.action instanceof StoredSpell) && o.action.the_spell && o.level === 0);
      } else {
        filtered = filtered.filter(o => o.action && (o.action instanceof CharacterSpell || o.action instanceof CharacterAbility || o.action instanceof StoredSpell) && o.action.the_spell && o.level === +view);
      }
    }
    if (!upcasts) {
      filtered = filtered.filter(o => o.lowest || (o.action instanceof StoredSpell));
    }
    // console.log(filtered.filter(o => o.name === "Detect Magic" || o.name === "Meld into Stone"));
    // Render them by level
    // Need to make it so SpellsAsAbilities 
    // that can be done sans slots 
    // show up at their appropriate level, 
    // so include them as well, 
    // even though they may not have slots of that level
    return (
      <div key="spell_groups">
        { Object.keys(levels).map(level => {
          if (["ALL"," Concentration "," Ritual "].includes(view) || view === level || (level === "0" && view === "Cantrip")) {
            const level_num = +level;
            if (isSpellLevel(level_num)) {
              return (
                <div key={level}>
                  { renderSpellsForLevelHead(filtered, level_num) }
                </div>
              );
            }
          } else {
            return null;
          }
        })}
      </div>
    );
  }

  const renderSpellsForLevelHead = (filtered: CharacterAction[], level_num: SpellLevel) => {
    let level_filtered: CharacterAction[] = [];
    // if (level_num === 2) {
    //   debugger;
    // }
    if (level_num === 0) {
      level_filtered = filtered.filter(o => 
        o.level === level_num);
    } else if (char_int.body.use_slots.filter(o => o.level === level_num).length > 0) {
      level_filtered = filtered.filter(o => 
        // (o.level === level_num && 
        //   o.action instanceof CharacterAbility &&
        //   o.action.the_ability instanceof SpellsFromList &&
        //   !["Normal","And Special Resource"].includes(o.action.the_ability.spell_overrides.slot_override)
        // ) || 
        (o.level === level_num && 
          o.action instanceof CharacterAbility &&
          o.action.sidekick_ability
        ) || 
        (o.level === level_num && 
          o.action instanceof CharacterAbility &&
          (o.action.the_ability instanceof SpellsFromList || 
            o.action.the_ability instanceof SpellsFromListWithCosts)
        ) || 
        ((o.action instanceof CharacterSpell || o.action instanceof StoredSpell) && o.level === level_num)
      );
    } else {
      level_filtered = filtered.filter(o => 
        o.level === level_num && 
        (
          (o.action instanceof CharacterAbility &&
            (o.action.the_ability instanceof SpellsFromList || 
              o.action.the_ability instanceof SpellsFromListWithCosts) &&
            !["Normal","And Special Resource"].includes(o.action.the_ability.spell_overrides.slot_override)) ||
          (o.action instanceof CharacterSpell && o.ritual_only)
        )
      );
    }
    // if (level_num === 2) {
    //   console.log(level_filtered);
    // }
    if (level_filtered.length > 0) {
      return (
        <>
          <Grid container spacing={0} direction="row"
            style={{
              fontSize: "14px",
              fontWeight: "bold",
            }}>
            <Grid
              size={{
                xs: 6
              }}>
              <span style={{ margin: "4px" }}>
                { level_num === 0 ? "Cantrip" : `${level_num}` }
              </span>
            </Grid>
            { level_num > 0 && renderSlotsForLevel(level_num) }
          </Grid>
          <Grid style={{ fontWeight: "bold" }} container spacing={0} direction="row">
            <Grid
              className="centered"
              size={{
                xs: 3
              }}>Name</Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 9
              }}>
              <Grid
                className="centered"
                size={{
                  xs: 3
                }}>Cost</Grid>
              <Grid
                className="centered"
                size={{
                  xs: 3
                }}>Range/Area</Grid>
              <Grid
                className="centered"
                size={{
                  xs: 3
                }}>Attack/DC</Grid>
              <Grid
                className="centered"
                size={{
                  xs: 3
                }}>Effect</Grid>
            </Grid>
          </Grid>
          { renderSpellsForLevelBody(level_filtered, level_num) }
        </>
      );
    } else {
      return null;
    }
  }

  const renderSlotsForLevel = (level: number) => {
    if (reloadingResources) {
      return null;
    } else {
      const slots = char_int.body.use_slots.filter(o => o.level === level).sort((a,b) => {
        return a.slot_name.localeCompare(b.slot_name)
      });
      return (
        <Grid size={6}>
          <div style={{ float: "right" }}>
            { slots.map(char_slot => {
              // console.log(char_slot);
              return (
                <span key={char_slot.resource_true_id}>
                  <CharacterResourceBoxes 
                    used={char_slot.used}
                    total={char_slot.total}
                    true_total={char_slot.true_total}
                    created={char_slot.created}
                    type={char_slot.slot_name}
                    char={char_int}
                    shared={shared}
                    onChange={async (used: number) => {
                      char_slot.used = used;
                      if (char_slot.is_resource) {
                        const char_resource = char_int.body.resources.find(o => o.type_id === char_slot.type_id);
                        if (char_resource) {
                          char_resource.used = 0;
                          char_resource.created = 0;
                        }
                      }
                      await char_util.resourcesChanged(char_int);
                      setPing(!ping);
                    }}
                  />
                </span>
              );
            })}
          </div>
        </Grid>
      );
    }
  }

  const renderSpellsForLevelBody = (level_filtered: CharacterAction[], level_num: SpellLevel) => {
    return (
      <Grid container spacing={0} direction="row">
        { level_filtered.sort((a,b) => { 
          return getName(a).localeCompare(getName(b));
        }).map(spell => {
          // if (level_num === 1 && spell.name.includes("Cure")) {
          //   debugger;
          // }
          if (reloading) {
            return null;
          } else {
            // if (spell.name.includes("Detect Magic")) {
            //   console.log(spell);
            // }
            return (
              <Grid
                key={`${spell.true_id}_${spell.type}_${spell.level}_${spell.slot}_${spell.ritual_only}`}
                size={12}>
                <CharacterActionInput
                  char_int={char_int}
                  char={char}
                  level={level_num}
                  action={spell}
                  show_casting_time
                  shared={shared}
                  onOpen={() => {
                    setDetailsAction(spell);
                    setDrawer("details");
                  }}
                />
              </Grid>
            );
          }
        })}
      </Grid>
    );
  }

  const renderExtras = () => {
    const action = details_action;
    const open = drawer === "details";
    if (action && open) {
      return (
        <>
          <ModalOrDrawer
            open={ drawer === "details" } 
            onClose={() => {
              setDrawer("");
              char_int.subscription_holder.modalClosed();
            }}>
            { (action.action instanceof CharacterSpell || action.action instanceof StoredSpell ||
              action.action instanceof CharacterAbility) ? 
              <CharacterSpellDetails
                char_int={char_int}
                obj={ action.action }
                level={ action.level }
                type={action.type}
                action={action}
                shared={shared}
                show_item
                onObjChange={(obj) => {
                  setReloading(true);
                }}
                onClose={() => {
                  setDrawer("");
                }}
              /> 
            : action.action instanceof CharacterItem && char_int instanceof Character ? 
              <CharacterItemDetailsSidebar
                char={char_int}
                char_item={action.action}
                shared={shared}
                onRemove={() => {
                  char_int.changed();
                  char_int.subscription_holder.resourcesChanged();
                  char_int.subscription_holder.recalculate();
                  setDrawer("");
                }}
              /> 
            : action.action instanceof Attack && 
              <CharacterAttackSidebar
                char={char_int}
                attack={action.action}
              />
            }
          </ModalOrDrawer>
        </>
      );
    }
    return null;
  }
  
  const enabled = !disabled && api.user !== null && (char_int.owner_id === api.user._id || (char_int instanceof Character && char_int.campaign && char_int.campaign.owner_id === api.user._id));
  return (
    <Grid container spacing={0} direction="row" className="curved_border bg_black_transp"
      style={{
        fontSize: "11px"
      }}>
      <Grid
        size={{
          xs: 12,
          sm: 4
        }}>{ renderSpellStats() }</Grid>
      <Grid
        size={{
          xs: 12,
          sm: 4
        }}>
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            <span className="header centered" style={{ fontSize: "14px" }}>Spells</span>
          </Grid>
          { enabled && !shared && char_int instanceof Character &&
            <Grid size={12}>
              <CharacterManageSpellsButton 
                char={char_int}
                enabled={ enabled && !shared }
              />
            </Grid>
          }
        </Grid>
      </Grid>
      <Grid
        size={{
          xs: 0,
          sm: 4
        }}></Grid>
      <Grid size={12}>
        <div style={{ margin: "4px" }}>
          <StringBox
            name="Search Spells"
            value={ search_string }
            onChange={(changed: string) => {
              setSearchString(changed);
              setReloading(true);
            }}
          />
        </div>
      </Grid>
      <Grid size={12}>
        { renderSpells() }
      </Grid>
      { renderExtras() }
    </Grid>
  );
}
